import { Button, Typography, useTheme } from '@mui/material';
import { Add, Insights } from '@mui/icons-material';
import { TEXTS } from '../../../core/texts';
import { ChangeEvent, useCallback, useState, FocusEvent } from 'react';
import { Modal } from '../../common/Modal';
import { ValidationErrorState } from '../../../types/validationErrorState';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../../common/boxes';
import { ButtonWithLoader } from '../../common/ButtonWithLoader';
import { Validator } from '../../../core/Validator';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../interfaces/errorResponse';
import { parseAxiosError } from '../../../services/axios';
import { ICreateProjectRequest } from '../../../interfaces/createProjectRequest';
import { ProjectOpportunityService } from '../../../services/project-opportunity';
import { ICreateProjectPosition, IProjectPosition } from '../../../interfaces/projectPosition';
import CreatePositions from './CreatePositions';
import NAMES = Validator.NAMES;
import { CLIENT_PROJECTS_QUERY_KEY } from '../../../queryHooks/client/useGetClientProjectOpportunities';
import { useNotification } from '../../../+state/useNotification';
import { useGetClientEntity } from '../../../queryHooks/client/useGetClientEntity';
import { ControlledInputOnBlur } from '../../common/ControlledInputOnBlur';

type PositionState = ICreateProjectPosition | IProjectPosition

export interface IPositionsState<T extends PositionState = ICreateProjectPosition> {
  [key: string]: T;
}

interface IState extends Omit<ICreateProjectRequest, 'positions'> {
  clientId: number | null;
}

const initialState: IState = {
  clientId: null,
  title: '',
  overview: '',
};

const CreateProjectOpportunity = ({ clientId }: {clientId: number | null}) => {
  const theme = useTheme();
  const { openErrorToast, openSuccessToast } = useNotification();
  const queryClient = useQueryClient();
  const [openedModal, setOpenedModal] = useState(false);
  const [state, setState] = useState<IState>({...initialState, clientId: clientId});
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [positions, setPositions] = useState<IPositionsState>({});
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});
  const [validationErrorsPositions, setValidationErrorsPositions] = useState<{ [key: string]: ValidationErrorState }>({});

  const toggleModal = useCallback(() => setOpenedModal(prev => {
    if (prev) {
      setValidationErrors({});
      setValidationErrorsPositions({});
      setPositions({});
      setState(initialState);
      setCustomerId(null);
    }
    return !prev;
  }), []);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState(prev => ({ ...prev, [name]: value }));
  }, []);

  const mutation = useMutation({
    mutationFn: () => ProjectOpportunityService.createNewProject(state.clientId!, {
      ...state,
      positions: Object.values(positions)
    }),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_SAVED);
      toggleModal();
      queryClient.invalidateQueries([CLIENT_PROJECTS_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const handleSubmitBtn = useCallback(() => {
    const validErrors = Validator.validate(state, NAMES.PROJECT);
    const positionsValidErrors = !Object.keys(positions).length
      ? { emptyPositions: { msg: 'Positions must be not empty' } }
      : Object.keys(positions).reduce((acc: { [p: string]: ValidationErrorState }, key) => {
        const mainErrors = Validator.validate(positions[key], NAMES.POSITION);
        const rateErrors = Validator.validate(positions[key].preferredRate, NAMES.RATE);
        const avblErrors = Validator.validate(positions[key].availability, NAMES.AVAILABILITY);
        const languageErrors = Validator.validate(positions[key].language, NAMES.LANGUAGE);
        if (mainErrors || rateErrors || avblErrors || languageErrors) {
          acc[key] = { ...languageErrors, ...avblErrors, ...rateErrors, ...mainErrors };
        }
        return acc;
      }, {});
    if (validErrors || (!!Object.keys(positionsValidErrors).length)) {
      setValidationErrors(validErrors || {});
      setValidationErrorsPositions(positionsValidErrors || {});
      return;
    }
    mutation.mutate();
  }, [mutation, state, positions]);

  const { data: customerData } = useGetClientEntity({
    id: customerId as number,
    enabled: customerId !== null,
    onSuccess: () => setValidationErrors(prev => ({ ...prev, clientId: null })),
    onError: (err: AxiosError) => setValidationErrors(prev => ({ ...prev, clientId: parseAxiosError(err) })),
  });

  const onBlurClientId = useCallback((e: FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCustomerId(value ? +value : null);
    if (!value) {
      setValidationErrors(prev => ({ ...prev, clientId: null }));
    }
  }, []);

  return (
    <>
      <Button startIcon={<Add/>} onClick={toggleModal} variant={'contained'}>{TEXTS.CREATE_NEW_PROJECT_OPPORTUNITY}</Button>
      {
        openedModal &&
        <Modal
          maxWidth={800} onClose={toggleModal}
          title={TEXTS.CREATE_NEW_PROJECT_OPPORTUNITY}
          height={'90vh'}
          headerIcon={<Insights color={'primary'}/>}
          dialogActions={<FlexBoxFullWidth justifyContent={'space-between'} alignItems={'center'}>
            <Button variant={'outlined'} color={'error'} sx={{ m: 1 }} onClick={toggleModal}>
              {TEXTS.CANCEL}
            </Button>
            <ButtonWithLoader variant={'outlined'} sx={{ m: 1 }} loading={mutation.isLoading} onClick={handleSubmitBtn}>
              {TEXTS.SAVE}
            </ButtonWithLoader>
          </FlexBoxFullWidth>}
        >
          <ColumnBoxFlexFullWidth>
            <ControlledInputOnBlur name={'clientId'} label={TEXTS.CLIENT_ID} required type={'number'} value={state.clientId || ''}
                       InputProps={{ inputProps: { min: 0 } }}
                       sx={{ mb: 2, '& p': { color: theme.palette.primary.dark } }} onChange={handleInputChange}
                       error={!!validationErrors.clientId}
                       helperText={validationErrors.clientId || (customerId ? customerData?.name : null)}
                       onBlur={onBlurClientId} color={'primary'}
            />

            <ControlledInputOnBlur name={'title'} label={TEXTS.TITLE} value={state.title}
                       error={!!validationErrors.title} helperText={validationErrors.title}
                       onChange={handleInputChange} sx={{ mb: 2 }}
            />
            <ControlledInputOnBlur name={'overview'} label={TEXTS.OVERVIEW} value={state.overview}
                       error={!!validationErrors.overview} helperText={validationErrors.overview}
                       onChange={handleInputChange} sx={{ mb: 2 }}
            />
            <CreatePositions positions={positions} setPositions={setPositions}
                             validationErrors={validationErrorsPositions}
                             setValidationErrors={setValidationErrorsPositions}/>
            {
              validationErrorsPositions.emptyPositions &&
              <Typography color={'error'} variant={'body2'}>{validationErrorsPositions.emptyPositions.msg}</Typography>
            }
          </ColumnBoxFlexFullWidth>
        </Modal>
      }
    </>
  );
};

export default CreateProjectOpportunity;